import { merge, pick } from 'lodash';

// @ts-check

/**
 * @typedef {Object} Margin
 * @property {number} left
 * @property {number} right
 * @property {number} top
 * @property {number} bottom
 */

/**
 * @typedef {Object} CalendarShapeSize
 * @property {number} [padding]
 * @property {number} [paddingLeft]
 * @property {number} [paddingRight]
 * @property {number} [paddingTop]
 * @property {number} [paddingBottom]
 * @property {number} [width]
 * @property {number} [height]
 * @property {Margin} [dayMargin]
 * @property {Margin} [dayPadding] Internal spacing between the day border and its content.
 */

export const getDefaultCalendarShapeSize = (calendarType = 1) => {
  let defaultCalendarShapeSize = {};

  // Settings for different calendar types
  if (calendarType === 1) {
    defaultCalendarShapeSize = {
      width: 300,
      height: 180,
      padding: 0,
      paddingLeft: 0,
      paddingRight: 0,
      paddingTop: 0,
      paddingBottom: 0,
      dayMargin: {
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
      },
      dayPadding: { left: 0, right: 0, top: 0, bottom: 0 },
    };
  } else if (calendarType === 2) {
    defaultCalendarShapeSize = {
      width: 300,
      height: 40,
      padding: 0,
      paddingLeft: 0,
      paddingRight: 0,
      paddingTop: 0,
      paddingBottom: 0,
      dayMargin: {
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
      },
      dayPadding: { left: 0, right: 0, top: 0, bottom: 0 },
    };
  }
  return defaultCalendarShapeSize;
};

/**
 * @param {Object} params
 * @param {import("actions/scene.types").CalendarType} [params.calendarType] calendar type
 * @param {Object} [params.calendarShape] calendar type
 * @param {CalendarShapeSize} [params.calendarShapeSize] calendar type
 * @param {Partial<Margin>} [params.weekdayNamePadding] Padding around each weekday name in calendar SVG units.
 * @param {number} [params.headerFontSize] Weekday header font size.
 */
export const getCalendarSettings = ({
  calendarType = 1,
  calendarShapeSize: _calendarShapeSize = {},
  weekdayNamePadding = {},
  headerFontSize = 10,
} = {}) => {
  // Default values for the number of days in a row and column
  /** @type {{ row: number; column: number }} count days in row and count days in column */
  let NumberDaysInRowAndColumn = { row: 7, column: 7 };

  // Settings for different calendar types
  if (calendarType === 1) {
    NumberDaysInRowAndColumn = { row: 7, column: 7 };
  } else if (calendarType === 2) {
    NumberDaysInRowAndColumn = { row: 2, column: 31 };
  }

  /** @type {Required<CalendarShapeSize>} */
  const calendarShapeSize = merge(getDefaultCalendarShapeSize(calendarType), _calendarShapeSize);

  const calendarShape = pick(calendarShapeSize, [
    'width',
    'height',
    'padding',
    'paddingLeft',
    'paddingRight',
    'paddingTop',
    'paddingBottom',
  ]);

  const DaysContainerShape = {
    width: calendarShape.width - calendarShapeSize.paddingLeft - calendarShapeSize.paddingRight,
    height: calendarShape.height - calendarShapeSize.paddingTop - calendarShapeSize.paddingBottom,
    margin: calendarShapeSize.padding,
    marginLeft: calendarShapeSize.paddingLeft,
    marginRight: calendarShapeSize.paddingRight,
    marginTop: calendarShapeSize.paddingTop,
    marginBottom: calendarShapeSize.paddingBottom,
  };

  const normalizePadding = (value) => (Number.isFinite(Number(value)) ? Math.max(0, Number(value)) : 0);
  const defaultRowHeight = DaysContainerShape.height / NumberDaysInRowAndColumn.row;
  const defaultHeaderPadding = {
    left: normalizePadding(calendarShapeSize.dayMargin.left),
    right: normalizePadding(calendarShapeSize.dayMargin.right),
    top: Math.round(
      Math.max(
        0,
        defaultRowHeight - normalizePadding(headerFontSize) - normalizePadding(calendarShapeSize.dayMargin.bottom),
      ),
    ),
    bottom: normalizePadding(calendarShapeSize.dayMargin.bottom),
  };
  const headerPadding = {
    left: normalizePadding(weekdayNamePadding.left ?? defaultHeaderPadding.left),
    right: normalizePadding(weekdayNamePadding.right ?? defaultHeaderPadding.right),
    top: normalizePadding(weekdayNamePadding.top ?? defaultHeaderPadding.top),
    bottom: normalizePadding(weekdayNamePadding.bottom ?? defaultHeaderPadding.bottom),
  };
  const headerHeight = Math.min(
    Math.max(0, DaysContainerShape.height),
    headerPadding.top + normalizePadding(headerFontSize) + headerPadding.bottom,
  );

  const normalizeSides = (sides) => ({
    left: normalizePadding(sides?.left),
    right: normalizePadding(sides?.right),
    top: normalizePadding(sides?.top),
    bottom: normalizePadding(sides?.bottom),
  });

  /** @type {{ width: number; height: number; margin: Margin; padding: Margin }} */
  const DayShape = {
    width: DaysContainerShape.width / NumberDaysInRowAndColumn.column,
    height: Math.max(0, DaysContainerShape.height - headerHeight) / (NumberDaysInRowAndColumn.row - 1),
    margin: normalizeSides(calendarShapeSize.dayMargin),
    padding: normalizeSides(calendarShapeSize.dayPadding),
  };
  // Content is inset by both the outer margin and the inner padding.
  const DayContentShape = {
    width: DayShape.width,
    height: DayShape.height,
    margin: {
      left: DayShape.margin.left + DayShape.padding.left,
      right: DayShape.margin.right + DayShape.padding.right,
      top: DayShape.margin.top + DayShape.padding.top,
      bottom: DayShape.margin.bottom + DayShape.padding.bottom,
    },
  };
  const WeekdayNameShape = { width: DayShape.width, height: headerHeight, margin: headerPadding };

  const calendarSettings = {
    daysOfWeek: 7,
    NumberDaysInRowAndColumn,
    DayShape,
    DayContentShape,
    WeekdayNameShape,
    DaysContainerShape,
    CalendarShape: calendarShape,
  };
  return calendarSettings;
};

/**
 * Applies date-number colours in order: current month, other month, then weekend.
 * Each enabled rule overrides the colour selected by the preceding rule.
 *
 * @param {Object} params
 * @param {import('actions/scene.types').CalendarShape} params.shape
 * @param {Date} params.day
 * @param {boolean} params.isCurrentMonth
 * @returns {string}
 */
export const getCalendarDayColor = ({ shape, day, isCurrentMonth }) => {
  let color = shape.mainColor;

  if (!isCurrentMonth && shape.useOtherMonthColor !== false) {
    color = shape.otherMonthColor || shape.secondaryColor || color;
  }

  const isWeekendDay = day.getDay() === 0 || day.getDay() === 6;
  if (isWeekendDay && shape.useWeekendColor === true) {
    color = shape.weekendColor || color;
  }

  return color;
};

/**
 * Calculates the local area available for event text inside a calendar day cell.
 * The area uses both day margin and padding; its top edge starts below the day number.
 *
 * @param {Object} params
 * @param {ReturnType<typeof getCalendarSettings>} params.calendarSettings
 * @param {number} params.dayFontSize
 * @param {number} [params.eventDateSpacing] Gap below the date number in calendar SVG units.
 * @returns {{ minX: number, minY: number, maxX: number, maxY: number, width: number, height: number }}
 */
export const getCalendarEventTextArea = ({ calendarSettings, dayFontSize, eventDateSpacing = 0 }) => {
  const { width: cellWidth, height: cellHeight, margin } = calendarSettings.DayContentShape;
  const normalizedDayFontSize = Number(dayFontSize) || 0;
  const minX = margin.left;
  const spacing = Number(eventDateSpacing);
  const minY = margin.top + normalizedDayFontSize + (Number.isFinite(spacing) ? Math.max(0, spacing) : 0);
  const maxX = cellWidth - margin.right;
  const maxY = cellHeight - margin.bottom;

  return {
    minX,
    minY,
    maxX,
    maxY,
    width: Math.max(0, maxX - minX),
    height: Math.max(0, maxY - minY),
  };
};
